import { DiscoveryApi } from '@backstage/core-plugin-api';
import { CustomAlertApi } from '@agilelab/plugin-wb-platform';
import {
  RequestOptions,
  SystemPrototype,
  SystemPrototypeApi,
} from '@agilelab/plugin-wb-system-prototype-common';

export class SystemPrototypeClient implements SystemPrototypeApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly alertApi: CustomAlertApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    alertApi: CustomAlertApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.alertApi = options.alertApi;
  }

  async deleteSystemPrototype(
    name: string,
    options: RequestOptions,
  ): Promise<void> {
    const allProtos = await this.getSystemPrototypes(options);
    const found = allProtos.find(
      p => p.name.toLowerCase() === name.toLowerCase(),
    );
    const base = await this.discoveryApi.getBaseUrl('system_prototype');
    const url = `${base}/system_prototype/${encodeURIComponent(
      found ? found.name : name,
    )}`;

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(options.token
            ? { Authorization: `Bearer ${options.token}` }
            : {}),
        },
      });

      if (!response.ok) {
        let errorMessage = `Failed to delete prototype: ${response.statusText}`;
        try {
          const responseText = await response.text();
          if (responseText) {
            try {
              const errorData = JSON.parse(responseText);
              if (errorData.message) {
                errorMessage = errorData.message;
              }
            } catch (e) {
              errorMessage = responseText;
            }
          }
        } catch (e) {
          errorMessage = `Failed to delete prototype: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const catalogBase = await this.discoveryApi.getBaseUrl('catalog');
      const refreshUrl = `${catalogBase}/system-prototype-provider/refresh`;

      const refreshResponse = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(options.token
            ? { Authorization: `Bearer ${options.token}` }
            : {}),
        },
      });

      if (!refreshResponse.ok) {
        this.alertApi.post({
          message: `Failed to trigger refresh: ${refreshResponse.statusText}`,
          severity: 'warning',
        });
      }
    } catch (error) {
      throw error;
    }
  }

  async createSystemPrototype(
    systemPrototype: SystemPrototype,
    options: RequestOptions,
  ): Promise<void> {
    const base = await this.discoveryApi.getBaseUrl('system_prototype');
    const url = `${base}/system_prototype`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      },
      body: JSON.stringify(systemPrototype),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.message?.includes('already exists')) {
        throw new Error('DUPLICATE_NAME');
      }
      throw new Error(`Failed to create prototype: ${response.statusText}`);
    }

    const catalogBase = await this.discoveryApi.getBaseUrl('catalog');
    const refreshUrl = `${catalogBase}/system-prototype-provider/refresh`;
    const refreshResponse = await fetch(refreshUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      },
    });
    if (!refreshResponse.ok) {
      this.alertApi.post({
        message: `Failed to trigger refresh: ${refreshResponse.statusText}`,
        severity: 'warning',
      });
    }
  }

  async getSystemPrototypes(
    options: RequestOptions,
    taxonomyRef?: string,
  ): Promise<SystemPrototype[]> {
    const base = await this.discoveryApi.getBaseUrl('system_prototype');
    const url = `${base}/system_prototype${
      taxonomyRef ? `?taxonomyRef=${encodeURIComponent(taxonomyRef)}` : ''
    }`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch prototypes: ${response.statusText}`);
    }
    return (await response.json()) as SystemPrototype[];
  }

  async updateSystemPrototype(
    id: number,
    data: Partial<SystemPrototype>,
    options: RequestOptions,
  ): Promise<void> {
    const base = await this.discoveryApi.getBaseUrl('system_prototype');
    const url = `${base}/system_prototype/${id}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.message?.includes('already exists')) {
        throw new Error('DUPLICATE_NAME');
      }
      throw new Error(`Failed to update prototype: ${response.statusText}`);
    }

    const catalogBase = await this.discoveryApi.getBaseUrl('catalog');
    const refreshUrl = `${catalogBase}/system-prototype-provider/refresh`;
    const refreshResponse = await fetch(refreshUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      },
    });
    if (!refreshResponse.ok) {
      this.alertApi.post({
        message: `Failed to trigger refresh: ${refreshResponse.statusText}`,
        severity: 'warning',
      });
    }
  }

  async getKeyResults(
    options: RequestOptions,
  ): Promise<Array<{ id: string; name: string; description: string }>> {
    const base = await this.discoveryApi.getBaseUrl('system_prototype');
    const url = `${base}/key_results`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch key results: ${response.statusText}`);
    }
    return response.json();
  }

  async getSystemPrototype(
    id: number,
    options: RequestOptions,
  ): Promise<SystemPrototype> {
    const base = await this.discoveryApi.getBaseUrl('system_prototype');
    const url = `${base}/system_prototype/${id}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch prototype: ${response.statusText}`);
    }
    return (await response.json()) as SystemPrototype;
  }
}
